import { Add, Close, Edit, TableRows } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  SxProps,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { TFunction } from 'i18next';
import { Moment } from 'moment';
import { useState } from 'react';
import * as React from 'react';
import { Control, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SolaForceHeading } from '~/components/SolaForceHeading';
import { EmpPosition } from '~/data/employee';
import { Enum, EnumListKey } from '~/data/enums';
import { useEnumFilteredOptions } from '~/features/OverlappingEmploymentDialog/hooks/useEnumFilteredOption';
import {
  OverlappingEmpForm,
  OverlappingEmploymentFormValues,
} from '~/features/OverlappingEmploymentDialog/hooks/useOverlappingEmploymentForm';
import { convertToMomentOrNull } from '~/utils/helpers/date';

import { useSxStyles } from '../useStyles';

interface Props {
  empId: number;
  empPosition: EmpPosition;
  formValues: OverlappingEmploymentFormValues;
}

export const AllowanceForm = ({ formValues }: Props) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const {
    formProps: { control, setValue },
  } = formValues;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'overlappingEmployment.allowance',
  });

  const [showRow, setShowRow] = useState(!!fields.length);

  const [currency, defaultStartDate] = useWatch({
    control,
    name: [
      'overlappingEmployment.basicData.fEmplCurrency',
      'overlappingEmployment.basicData.fEmplDateStart',
    ],
  });

  const [filteredOptionsAllowanceType, filteredOptionsAllowanceFrequency] =
    useEnumFilteredOptions(control, [
      EnumListKey.ALLOWANCE_TYPE,
      EnumListKey.EVENT_FREQUENCY,
    ]);

  const handleAddRow = () => {
    setShowRow(true);
    append({
      fAllowanceProposalType: '',
      fAllowanceProposalStartDate: defaultStartDate,
      fAllowanceProposalEndDate: '',
      fAllowanceProposalAmount: undefined,
      fAllowanceProposalTaxableAmount: undefined,
      fAllowanceProposalFrequency: '',
      fAllowanceProposalInfo: '',
    });
  };

  const handleRemoveRow = (row: number) => {
    remove(row);
  };

  const onCancel = () => {
    setShowRow(false);
    setValue('overlappingEmployment.allowance', []);
  };

  return (
    <Box>
      <SolaForceHeading headingIcon={<TableRows />} heading={t('laAllowance')}>
        <Button startIcon={<Add />} onClick={handleAddRow}>
          {t('laAdd')}
        </Button>
        {!showRow ? (
          <Button onClick={() => setShowRow(true)} startIcon={<Edit />}>
            {t('laEdit')}
          </Button>
        ) : (
          <Button onClick={onCancel} startIcon={<Close />}>
            {t('laCancel')}
          </Button>
        )}
      </SolaForceHeading>
      {showRow && (
        <Grid sx={{ overflowX: 'auto' }}>
          <Box sx={sxStyles.table} component={'table'}>
            <Box component={'thead'}>
              <Box component={'tr'}>
                <Box component={'th'}>
                  {t('laAllowanceType')}{' '}
                  <Box component={'i'} sx={sxStyles.required}></Box>
                </Box>
                <Box component={'th'}>
                  {t('laStartDate')}{' '}
                  <Box component={'i'} sx={sxStyles.required}></Box>
                </Box>
                <Box component={'th'}>{t('laEndDate')}</Box>
                <Box component={'th'}>{t('laAllowance')}</Box>
                <Box component={'th'}>{t('laTaxable')}</Box>
                <Box component={'th'}>
                  {t('laFrequency')}{' '}
                  <Box component={'i'} sx={sxStyles.required}></Box>
                </Box>
                <Box component={'th'}>{t('laAllowanceInfo')}</Box>
                <Box component={'th'}></Box>
              </Box>
            </Box>
            <Box component={'tbody'}>
              {fields.map((_, index) => {
                return renderAllowanceRow(
                  index,
                  [
                    filteredOptionsAllowanceType,
                    filteredOptionsAllowanceFrequency,
                  ],
                  control,
                  sxStyles,
                  currency || '',
                  t,
                  handleRemoveRow,
                );
              })}
            </Box>
          </Box>
        </Grid>
      )}
    </Box>
  );
};

const renderAllowanceRow = (
  index: number,
  optionsArr: Enum[][],
  control: Control<OverlappingEmpForm>,
  sxStyles: Record<string, SxProps>,
  currency: string,
  t: TFunction,
  onRemove: (index: number) => void,
) => {
  const dateFormat = 'YYYY-MM-DD';

  const requiredText = t('fieldIsRequired');

  const [filteredOptionsAllowanceType, filteredOptionsAllowanceFrequency] =
    optionsArr;

  return (
    <Box key={index} component={'tr'}>
      <Box component={'td'}>
        <Controller
          control={control}
          name={`overlappingEmployment.allowance.${index}.fAllowanceProposalType`}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              options={filteredOptionsAllowanceType}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(o, v) => o?.code === v?.code}
              onChange={(_, value) => onChange(value?.code || '')}
              value={
                filteredOptionsAllowanceType.find((o) => o?.code === value) ||
                null
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                  sx={sxStyles.inputField}
                />
              )}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <Controller
          name={`overlappingEmployment.allowance.${index}.fAllowanceProposalStartDate`}
          control={control}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DatePicker
              value={convertToMomentOrNull(value)}
              onChange={(v: Moment | null) => {
                onChange(v?.format(dateFormat));
              }}
              slotProps={{
                textField: {
                  required: true,
                  error: !!error,
                  helperText: error?.message,
                  size: 'small',
                  sx: sxStyles.inputField,
                },
              }}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <Controller
          name={`overlappingEmployment.allowance.${index}.fAllowanceProposalEndDate`}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DatePicker
              value={convertToMomentOrNull(value)}
              onChange={(v: Moment | null) => {
                onChange(v?.format(dateFormat));
              }}
              slotProps={{
                textField: {
                  error: !!error,
                  helperText: error?.message,
                  size: 'small',
                  sx: sxStyles.inputField,
                },
              }}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <Controller
          control={control}
          name={`overlappingEmployment.allowance.${index}.fAllowanceProposalAmount`}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="number"
              value={value}
              onChange={onChange}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">{currency}</InputAdornment>
                ),
              }}
              sx={sxStyles.inputField}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <Controller
          control={control}
          name={`overlappingEmployment.allowance.${index}.fAllowanceProposalTaxableAmount`}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="number"
              value={value}
              onChange={onChange}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">{currency}</InputAdornment>
                ),
              }}
              sx={sxStyles.inputField}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <Controller
          control={control}
          name={`overlappingEmployment.allowance.${index}.fAllowanceProposalFrequency`}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              options={filteredOptionsAllowanceFrequency}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(o, v) => o?.code === v?.code}
              onChange={(_, value) => onChange(value?.code || '')}
              value={
                filteredOptionsAllowanceFrequency.find(
                  (o) => o?.code === value,
                ) || null
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                  sx={sxStyles.inputField}
                />
              )}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <Controller
          control={control}
          name={`overlappingEmployment.allowance.${index}.fAllowanceProposalInfo`}
          render={({ field: { onChange, value } }) => (
            <TextField
              value={value}
              multiline
              rows={1}
              size="small"
              onChange={onChange}
              sx={sxStyles.inputField}
            />
          )}
        />
      </Box>
      <Box component={'td'}>
        <IconButton
          size="small"
          onClick={() => onRemove(index)}
          sx={sxStyles.deleteButton}
        >
          <Box component={'i'} className="fa fa-trash"></Box>
        </IconButton>
      </Box>
    </Box>
  );
};
